import FlashOnIcon from '@mui/icons-material/FlashOn';
import {
  EquityColumnGroup,
  ScannerID,
  ScannerColumnVisibility,
  EquityFieldKey,
  ScannerListItem,
} from '../types';
import CompressIcon from '@mui/icons-material/Compress';
import FlashOffIcon from '@mui/icons-material/FlashOff';
import ExponentialGrowth from '../icons/ExponentialGrowth';
import WhatshotOutlinedIcon from '@mui/icons-material/WhatshotOutlined';
import TrendingUp from '@mui/icons-material/TrendingUp';
import TrendingDown from '@mui/icons-material/TrendingDown';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import Shield from '@mui/icons-material/Shield';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import PercentIcon from '@mui/icons-material/Percent';
import CategoryIcon from '@mui/icons-material/Category';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import GridViewIcon from '@mui/icons-material/GridView';

// TODO: Investigate fixing `putctrl`/Pivot calculation.  For now, suppressing it in
// the UX

export const FIELD_NAME_MAPPING = {
  upx: 'Last Closing',
  maxfs: 'Hedge Wall',
  keyg: 'Key Gamma Strike',
  keyd: 'Key Delta Strike',
  // putctrl: 'Pivot',
  cws: 'Call Wall',
  pws: 'Put Wall',
  high_vol_point: 'High Volatility Point',
  low_vol_point: 'Low Volatility Point',
  sig_high: 'SG Implied 1d Move High',
  sig_low: 'SG Implied 1d Move Low',
};

export const REVERSE_FIELD_NAME_MAPPING = Object.fromEntries(
  Object.entries(FIELD_NAME_MAPPING).map(([k, v]) => [v, k]),
);

export const SYNTH_OI_FIELDS = {
  upx: 'Last Close',
  high_vol_point: 'High Volatility Point',
  low_vol_point: 'Low Volatility Point',
};

export const SCANNER_NAME_MAPPING: Record<ScannerID, string> = {
  [ScannerID.CLOSE_TO_HEDGE_WALL]: '1% Margin of Hedge Wall',
  [ScannerID.TOP_GAMMA_EXP]: 'Top Gamma % Expiring this Friday',
  [ScannerID.TOP_DELTA_EXP]: 'Top Delta % Expiring this Friday',
  [ScannerID.CLOSE_TO_KEY_DELTA_STRIKE]: '1% Margin of Key Delta Strike',
  [ScannerID.VOL_RISK_PREMIUM]: 'Volatility Risk Premium',
  [ScannerID.SQUEEZE]: 'Squeeze',
  [ScannerID.CROSS_ASSET_SUMMARY]: 'Cross Asset Summary',
  [ScannerID.EARNINGS_IV_CRUSH]: 'Earnings IV Crush',
  [ScannerID.HIGHEST_OPTIONS_IMPACT]: 'Highest Options Impact',
  [ScannerID.REVERSE_VOL_RISK_PREMIUM]: 'Reverse Volatility Risk Premium',
  [ScannerID.CALL_WALL_INCREASE]: 'Call Wall Increase',
  [ScannerID.CALL_WALL_DECREASE]: 'Call Wall Decrease',
  [ScannerID.PUT_WALL_INCREASE]: 'Put Wall Increase',
  [ScannerID.PUT_WALL_DECREASE]: 'Put Wall Decrease',
  [ScannerID.HEDGE_WALL_INCREASE]: 'Hedge Wall Increase',
  [ScannerID.HEDGE_WALL_DECREASE]: 'Hedge Wall Decrease',
  [ScannerID.IV_PERCENT_CHANGE]: 'IV Percent Change',
  [ScannerID.SECTOR_ETFS]: 'Sector ETFs',
};

export const SCANNER_DESCRIPTION_MAPPING: Record<ScannerID, string> = {
  [ScannerID.CLOSE_TO_HEDGE_WALL]:
    'Stocks may find options based support or resistance at this level, which could be a basis for a variety of different trades.',
  [ScannerID.TOP_GAMMA_EXP]:
    'This level can act as a magnet, if a large position (x >=30% of gamma expiring). This magnet can release following expiration.',
  [ScannerID.TOP_DELTA_EXP]:
    'This filter can indicate stocks which may reverse direction following upcoming options expirations (x > 30% of delta expiring).',
  [ScannerID.CLOSE_TO_KEY_DELTA_STRIKE]:
    'Stocks which are nearing a critical hedging level for dealers. This level could indicate a point of stock price acceleration or pinning.',
  [ScannerID.VOL_RISK_PREMIUM]:
    'The Volatility Risk Premium scanner highlights names that have unusually expensive options, indicating potential opportunities to sell premium.',
  [ScannerID.SQUEEZE]:
    'The Squeeze Scanner highlights stocks that have potential for an explosive upside move based on short interest, gamma levels, options volume, and a proprietary SpotGamma formula.',
  [ScannerID.CROSS_ASSET_SUMMARY]:
    'Shows a list of symbols across stocks, bonds, and commodities to give you an overview of the current market dynamics across multiple asset classes.',
  [ScannerID.EARNINGS_IV_CRUSH]:
    'Stocks that have IV in contango with significant underlying stock volume and whose earnings date comes after next expiration',
  [ScannerID.HIGHEST_OPTIONS_IMPACT]:
    'The Options Impact scanner flags the top 50 names where options hedging pressure is exerting substantial influence relative to the volume traded.',
  [ScannerID.REVERSE_VOL_RISK_PREMIUM]:
    'The Reverse VRP (Volatility Risk Premium) scanner highlights names that have unusually cheap premium, indicating a potential opportunity to buy options.',
  [ScannerID.CALL_WALL_INCREASE]: `This scanner displays the 25 names that have shown the largest day-over-day increase in the stock's Call Wall`,
  [ScannerID.CALL_WALL_DECREASE]: `This scanner displays the 25 names that have shown the largest day-over-day decrease in the stock's Call Wall`,
  [ScannerID.PUT_WALL_INCREASE]: `This scanner displays the 25 names that have shown the largest day-over-day increase in the stock's Put Wall`,
  [ScannerID.PUT_WALL_DECREASE]: `This scanner displays the 25 names that have shown the largest day-over-day decrease in the stock's Put Wall`,
  [ScannerID.HEDGE_WALL_INCREASE]: `This scanner displays the 25 names that have shown the largest day-over-day increase in the stock's Hedge Wall`,
  [ScannerID.HEDGE_WALL_DECREASE]: `This scanner displays the 25 names that have shown the largest day-over-day decrease in the stock's Hedge Wall`,
  [ScannerID.IV_PERCENT_CHANGE]:
    'This scanner flags the top 50 names that have experienced the largest change to their 30-day at-the-money implied volatility',
  [ScannerID.SECTOR_ETFS]:
    'The Sectors list displays the 11 ETFs that track the major economic sectors',
};

export const defaultColumnScannerVisibility = Object.values(
  EquityFieldKey,
).reduce((acc, key) => {
  acc[key] = true;
  return acc;
}, {} as ScannerColumnVisibility);

export const LEGACY_ONLY_SCANNERS = [
  ScannerID.SQUEEZE,
  ScannerID.REVERSE_VOL_RISK_PREMIUM,
  ScannerID.CALL_WALL_INCREASE,
  ScannerID.CALL_WALL_DECREASE,
  ScannerID.PUT_WALL_INCREASE,
  ScannerID.PUT_WALL_DECREASE,
  ScannerID.HEDGE_WALL_INCREASE,
  ScannerID.HEDGE_WALL_DECREASE,
];

export const SG_SPECIAL_SCANNERS: ScannerListItem[] = [
  {
    label: {
      icon: <FlashOnIcon fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.VOL_RISK_PREMIUM],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.VOL_RISK_PREMIUM],
    },
    scanner: ScannerID.VOL_RISK_PREMIUM,
  },
  {
    label: {
      icon: <ExponentialGrowth fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.SQUEEZE],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.SQUEEZE],
    },
    scanner: ScannerID.SQUEEZE,
  },
  {
    label: {
      icon: <FlashOffIcon fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.REVERSE_VOL_RISK_PREMIUM],
      description:
        SCANNER_DESCRIPTION_MAPPING[ScannerID.REVERSE_VOL_RISK_PREMIUM],
    },
    scanner: ScannerID.REVERSE_VOL_RISK_PREMIUM,
  },
  {
    label: {
      icon: <WhatshotOutlinedIcon fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.HIGHEST_OPTIONS_IMPACT],
      description:
        SCANNER_DESCRIPTION_MAPPING[ScannerID.HIGHEST_OPTIONS_IMPACT],
    },
    scanner: ScannerID.HIGHEST_OPTIONS_IMPACT,
  },
  {
    label: {
      icon: <CompressIcon fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.EARNINGS_IV_CRUSH],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.EARNINGS_IV_CRUSH],
    },
    scanner: ScannerID.EARNINGS_IV_CRUSH,
  },
  {
    label: {
      icon: <TrendingUp fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.CALL_WALL_INCREASE],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.CALL_WALL_INCREASE],
    },
    scanner: ScannerID.CALL_WALL_INCREASE,
  },
  {
    label: {
      icon: <TrendingDown fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.CALL_WALL_DECREASE],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.CALL_WALL_DECREASE],
    },
    scanner: ScannerID.CALL_WALL_DECREASE,
  },
  {
    label: {
      icon: <ArrowUpward fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.PUT_WALL_INCREASE],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.PUT_WALL_INCREASE],
    },
    scanner: ScannerID.PUT_WALL_INCREASE,
  },
  {
    label: {
      icon: <ArrowDownward fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.PUT_WALL_DECREASE],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.PUT_WALL_DECREASE],
    },
    scanner: ScannerID.PUT_WALL_DECREASE,
  },
  {
    label: {
      icon: <Shield fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.HEDGE_WALL_INCREASE],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.HEDGE_WALL_INCREASE],
    },
    scanner: ScannerID.HEDGE_WALL_INCREASE,
  },
  {
    label: {
      icon: <ShieldOutlined fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.HEDGE_WALL_DECREASE],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.HEDGE_WALL_DECREASE],
    },
    scanner: ScannerID.HEDGE_WALL_DECREASE,
  },
  {
    label: {
      icon: <GppMaybeIcon fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.CLOSE_TO_HEDGE_WALL],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.CLOSE_TO_HEDGE_WALL],
    },
    scanner: ScannerID.CLOSE_TO_HEDGE_WALL,
  },
];

export const SG_STOCK_SCANNERS: ScannerListItem[] = [
  {
    label: {
      icon: <PercentIcon fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.IV_PERCENT_CHANGE],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.IV_PERCENT_CHANGE],
    },
    scanner: ScannerID.IV_PERCENT_CHANGE,
  },
  {
    label: {
      icon: <CategoryIcon fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.SECTOR_ETFS],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.SECTOR_ETFS],
    },
    scanner: ScannerID.SECTOR_ETFS,
  },
  {
    label: {
      icon: <TrendingUpIcon fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.TOP_GAMMA_EXP],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.TOP_GAMMA_EXP],
    },
    scanner: ScannerID.TOP_GAMMA_EXP,
  },
  {
    label: {
      icon: <TimelineIcon fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.TOP_DELTA_EXP],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.TOP_DELTA_EXP],
    },
    scanner: ScannerID.TOP_DELTA_EXP,
  },
  {
    label: {
      icon: <GridViewIcon fontSize="small" />,
      name: SCANNER_NAME_MAPPING[ScannerID.CROSS_ASSET_SUMMARY],
      description: SCANNER_DESCRIPTION_MAPPING[ScannerID.CROSS_ASSET_SUMMARY],
    },
    scanner: ScannerID.CROSS_ASSET_SUMMARY,
  },
];

export const COLUMN_TO_GROUP_MAPPING = {
  trade_date: EquityColumnGroup.STATISTICS,
  earnings_utc: EquityColumnGroup.STATISTICS,
  sym: EquityColumnGroup.STATISTICS,
  price: EquityColumnGroup.STATISTICS,
  upx: EquityColumnGroup.STATISTICS,
  total_volume: EquityColumnGroup.STATISTICS,
  stock_volume: EquityColumnGroup.STATISTICS,
  [EquityFieldKey.high_vol_point]: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  [EquityFieldKey.low_vol_point]: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  [EquityFieldKey.optionsImpact]: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  keyg: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  keyd: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  maxfs: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  cws: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  pws: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  atmgc: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  atmgp: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  atmdc: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  atmdp: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  next_exp_g: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  next_exp_d: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  max_exp_g_date: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  max_exp_d_date: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  cv: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  pv: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  ne_call_volume: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  ne_put_volume: EquityColumnGroup.SG_KEY_DAILY_LEVELS,
  put_call_ratio: EquityColumnGroup.DIRECTIONAL_INDICATORS,
  volume_ratio: EquityColumnGroup.DIRECTIONAL_INDICATORS,
  gamma_ratio: EquityColumnGroup.DIRECTIONAL_INDICATORS,
  delta_ratio: EquityColumnGroup.DIRECTIONAL_INDICATORS,
  ne_skew: EquityColumnGroup.VOLATILITY_INSIGHTS,
  skew: EquityColumnGroup.VOLATILITY_INSIGHTS,
  rv30: EquityColumnGroup.VOLATILITY_INSIGHTS,
  atm_iv30: EquityColumnGroup.VOLATILITY_INSIGHTS,
  iv_rank: EquityColumnGroup.VOLATILITY_INSIGHTS,
  garch_rank: EquityColumnGroup.VOLATILITY_INSIGHTS,
  skew_rank: EquityColumnGroup.VOLATILITY_INSIGHTS,
  options_implied_move: EquityColumnGroup.VOLATILITY_INSIGHTS,
  dpi: EquityColumnGroup.DARK_POOL_INDICATORS,
  dpi_high52w: EquityColumnGroup.STATISTICS,
  dpi_low52w: EquityColumnGroup.STATISTICS,
  dpi_volume: EquityColumnGroup.DARK_POOL_INDICATORS,
  hist_dpi: EquityColumnGroup.DARK_POOL_INDICATORS,
  hist_dpi_volume: EquityColumnGroup.DARK_POOL_INDICATORS,
};

export const EQUITYHUB_TAB_ZENDESK_MAPPING: Record<string, string> = {
  composite: 'Composite View',
  pc_impact: 'Put & Call Impact',
  live_price: 'Live Price & SG Levels',
  skew: 'Skew Chart',
};

export const EQUITYHUB_UPSELL = {
  title: 'Unlock Access with SpotGamma Essential',
  subtitle: "With a SpotGamma Essential subscription, you'll get:",
  items: [
    'Key levels for 3,500+ stocks & indices',
    'Personalized watchlist to monitor tickers',
    'Highly actionable alerts for options and equity traders',
  ],
};

export const EQUITYHUB_SYNTH_OI_UPSELL = {
  title: 'Unlock Unlimited Synthetic OI Access',
  subtitle: "With a SpotGamma Alpha subscription, you'll get:",
  upgrade_subtitle: "Upgrade to a SpotGamma Alpha subscription, you'll get:",
  items: [
    'Real-time charts incorporating all options trades',
    'Proprietary & assumption-free data on market maker behavior',
    'Highly actionable alerts for options and equity traders',
    'Key levels from our proprietary positioning model',
  ],
};

export const WATCHLISTS_UPSELL = {
  title: 'Create and Save Custom Watchlists',
  subtitle:
    'Upgrade to create unlimited custom watchlists and save your favorite symbols for quick access',
  upgrade_subtitle:
    "With a SpotGamma subscription, you'll get access to unlimited custom watchlists and advanced symbol management",
  items: [
    'Create unlimited custom watchlists',
    'Save and organize your favorite symbols',
    'Quick access to your saved watchlists',
  ],
};

// TODO: pull this from the middle layer so that changes are reflected more quickly
export const CROSS_ASSET_SUMMARY_SYMS = [
  'SPX',
  'NDX',
  'IWM',
  'ESX',
  'FXI',
  'VIX',
  'GLD',
  'SLV',
  'USO',
  'UNG',
  'URA',
  'HGA',
  'TLT',
  'HYG',
  'ARKK',
  'XLE',
  'XLF',
  'XLV',
  'XME',
];

export const EH_LEGACY_MAIN_EQUITY_DETAILS_FIELDS: EquityFieldKey[] = [
  EquityFieldKey.price,
  EquityFieldKey.dailyChange,
  EquityFieldKey.prevClose,
  EquityFieldKey.earningsDate,
  EquityFieldKey.callWall,
  EquityFieldKey.putWall,
  EquityFieldKey.skewRank,
  EquityFieldKey.ivRank,
];

export const EH_LEGACY_EXPANDED_EQUITY_DETAILS_FIELDS: EquityFieldKey[] = [
  EquityFieldKey.callGamma,
  EquityFieldKey.putGamma,
  EquityFieldKey.topGammaExp,
  EquityFieldKey.topDeltaExp,
  EquityFieldKey.callVolume,
  EquityFieldKey.putVolume,
  EquityFieldKey.putCallRatio,
  EquityFieldKey.rv30,
  EquityFieldKey.iv30,
  EquityFieldKey.ivRank,
  EquityFieldKey.garchRank,
  EquityFieldKey.optionsImpliedMove,
];

export const EH_ALL_LEGACY_EQUITY_DETAILS_FIELDS = [
  ...EH_LEGACY_MAIN_EQUITY_DETAILS_FIELDS,
  ...EH_LEGACY_EXPANDED_EQUITY_DETAILS_FIELDS,
  EquityFieldKey.stock_volume,
  EquityFieldKey.wkHigh52,
  EquityFieldKey.wkLow52,
  EquityFieldKey.keyGammaStr,
  EquityFieldKey.keyDeltaStr,
  EquityFieldKey.hedgeWall,
  EquityFieldKey.nextExpGamma,
  EquityFieldKey.nextExpDelta,
  EquityFieldKey.putCallVolRatio,
  EquityFieldKey.gammaRatio,
  EquityFieldKey.deltaRatio,
  EquityFieldKey.neSkew,
  EquityFieldKey.skew,
  EquityFieldKey.skewRank,
  EquityFieldKey.neCallVolume,
  EquityFieldKey.nePutVolume,
  EquityFieldKey.dpi,
  EquityFieldKey.dpiVol,
  EquityFieldKey.dpi5day,
  EquityFieldKey.dpi5dayVol,
];

export const EH_SYNTH_OI_MAIN_EQUITY_DETAILS_FIELDS: EquityFieldKey[] = [
  EquityFieldKey.optionsImpact,
  EquityFieldKey.price,
  EquityFieldKey.dailyChange,
  EquityFieldKey.prevClose,
  EquityFieldKey.earningsDate,
  EquityFieldKey.high_vol_point,
  EquityFieldKey.low_vol_point,
];

export const EH_SYNTH_OI_EXPANDED_EQUITY_DETAILS_FIELDS: EquityFieldKey[] = [
  EquityFieldKey.callGamma,
  EquityFieldKey.putGamma,
  EquityFieldKey.topGammaExp,
  EquityFieldKey.topDeltaExp,
  EquityFieldKey.callVolume,
  EquityFieldKey.putVolume,
  EquityFieldKey.putCallRatio,
  EquityFieldKey.rv30,
  EquityFieldKey.iv30,
  EquityFieldKey.ivRank,
  EquityFieldKey.garchRank,
  EquityFieldKey.skewRank,
  EquityFieldKey.optionsImpliedMove,
];

export const EH_ALL_SYNTH_OI_EQUITY_DETAILS_FIELDS = [
  ...EH_SYNTH_OI_MAIN_EQUITY_DETAILS_FIELDS,
  ...EH_SYNTH_OI_EXPANDED_EQUITY_DETAILS_FIELDS,
  EquityFieldKey.nextExpGamma,
  EquityFieldKey.nextExpDelta,
  EquityFieldKey.gammaRatio,
  EquityFieldKey.deltaRatio,
  EquityFieldKey.neSkew,
  EquityFieldKey.skew,
  EquityFieldKey.neCallVolume,
  EquityFieldKey.nePutVolume,
];

export const EQUITY_FIELD_LABEL_MAPPING: Record<EquityFieldKey, string> = {
  [EquityFieldKey.sym]: 'Symbol',
  [EquityFieldKey.price]: 'Current Price',
  [EquityFieldKey.dailyChange]: 'Daily Change',
  [EquityFieldKey.prevClose]: 'Previous Close',
  [EquityFieldKey.stock_volume]: 'Stock Volume',
  [EquityFieldKey.wkHigh52]: '52 Week High',
  [EquityFieldKey.wkLow52]: '52 Week Low',
  [EquityFieldKey.earningsDate]: 'Earnings Date',
  [EquityFieldKey.keyGammaStr]: 'Key Gamma Strike',
  [EquityFieldKey.keyDeltaStr]: 'Key Delta Strike',
  [EquityFieldKey.hedgeWall]: 'Hedge Wall',
  [EquityFieldKey.prevHedgeWall]: 'Prev Hedge Wall',
  [EquityFieldKey.callWall]: 'Call Wall',
  [EquityFieldKey.prevCallWall]: 'Prev Call Wall',
  [EquityFieldKey.putWall]: 'Put Wall',
  [EquityFieldKey.prevPutWall]: 'Prev Put Wall',
  [EquityFieldKey.callGamma]: 'Call Gamma',
  [EquityFieldKey.putGamma]: 'Put Gamma',
  [EquityFieldKey.callDelta]: 'Call Delta',
  [EquityFieldKey.putDelta]: 'Put Delta',
  [EquityFieldKey.nextExpGamma]: 'Next Exp Gamma',
  [EquityFieldKey.nextExpDelta]: 'Next Exp Delta',
  [EquityFieldKey.topGammaExp]: 'Top Gamma Exp',
  [EquityFieldKey.topDeltaExp]: 'Top Delta Exp',
  [EquityFieldKey.callVolume]: 'Call Volume',
  [EquityFieldKey.putVolume]: 'Put Volume',
  [EquityFieldKey.neCallVolume]: 'Next Exp Call Vol',
  [EquityFieldKey.nePutVolume]: 'Next Exp Put Vol',
  [EquityFieldKey.putCallRatio]: 'Put/Call OI\u00a0Ratio',
  [EquityFieldKey.putCallVolRatio]: 'Volume Ratio',
  [EquityFieldKey.gammaRatio]: 'Gamma Ratio',
  [EquityFieldKey.deltaRatio]: 'Delta Ratio',
  [EquityFieldKey.neSkew]: 'NE Skew',
  [EquityFieldKey.skew]: 'Skew',
  [EquityFieldKey.rv30]: '1 M RV',
  [EquityFieldKey.iv30]: '1 M IV',
  [EquityFieldKey.ivRank]: 'IV Rank',
  [EquityFieldKey.garchRank]: 'Garch Rank',
  [EquityFieldKey.skewRank]: 'Skew Rank',
  [EquityFieldKey.optionsImpliedMove]: 'Options Implied Move',
  [EquityFieldKey.dpi]: 'DPI',
  [EquityFieldKey.quoteDate]: 'Trade Date',
  [EquityFieldKey.name]: 'Name',
  [EquityFieldKey.callsum]: 'Call OI',
  [EquityFieldKey.putsum]: 'Put OI',
  [EquityFieldKey.minfs]: 'Min Fs',
  [EquityFieldKey.largeCoi]: 'Large Coi',
  [EquityFieldKey.largePoi]: 'Large Poi',
  [EquityFieldKey.totaldelta]: 'Total Delta',
  [EquityFieldKey.d95ne]: '95% Next Exp Delta',
  [EquityFieldKey.d25ne]: '25% Next Exp Delta',
  [EquityFieldKey.d95]: '95% Delta',
  [EquityFieldKey.d25]: '25% Delta',
  [EquityFieldKey.strike_list]: 'Strike List',
  [EquityFieldKey.cg_list]: 'Call Greeks List',
  [EquityFieldKey.pg_list]: 'Put Greeks List',
  [EquityFieldKey.mf_list]: 'MF List',
  [EquityFieldKey.smf_list]: 'SMF List',
  [EquityFieldKey.putctrl]: 'Put Ctrl',
  [EquityFieldKey.activity_factor]: 'Activity Factor',
  [EquityFieldKey.position_factor]: 'Position Factor',
  [EquityFieldKey.call_strikes_list_absg]: 'Call Strikes List Absg',
  [EquityFieldKey.call_gnot_list_absg]: 'Call Gnot List Absg',
  [EquityFieldKey.put_strikes_list_absg]: 'Put Strikes List Absg',
  [EquityFieldKey.put_gnot_list_absg]: 'Put Gnot List Absg',
  [EquityFieldKey.date]: 'Date',
  [EquityFieldKey.Symbol]: 'Symbol',
  [EquityFieldKey.dpi_sector]: 'Sector',
  [EquityFieldKey.live]: 'Live',
  [EquityFieldKey.vrp_scanner]: 'VRP Scanner',
  [EquityFieldKey.vrp_scanner_high]: 'VRP Scanner High',
  [EquityFieldKey.squeeze_scanner]: 'Squeeze Scanner',
  [EquityFieldKey.call_oi]: 'Call OI',
  [EquityFieldKey.call_gamma_abs]: 'Call Gamma Abs',
  [EquityFieldKey.put_oi]: 'Put OI',
  [EquityFieldKey.put_gamma_abs]: 'Put Gamma Abs',
  [EquityFieldKey.iv_pct]: 'IV %',
  [EquityFieldKey.cskew_pct]: 'Call Skew %',
  [EquityFieldKey.pskew_pct]: 'Put Skew %',
  [EquityFieldKey.dpiVol]: '% DPI Volume',
  [EquityFieldKey.dpi5day]: '5 day DPI',
  [EquityFieldKey.dpi5dayVol]: '5d % DPI Volume',
  [EquityFieldKey.low_vol_point]: 'Low Vol Point',
  [EquityFieldKey.high_vol_point]: 'High Vol Point',
  [EquityFieldKey.optionsImpact]: 'Options Impact',
  [EquityFieldKey.stock_volume_30d_avg]: '30d Avg Stock Volume',
  [EquityFieldKey.ne_date]: 'Next Exp Date',
  [EquityFieldKey.iv_slope_ne_45]: 'IV Slope NE 45',
  [EquityFieldKey.atm_iv30_pct_chg]: '% 30DTE IV Change from Yesterday',
};

export const SECTOR_ETFS = [
  'XLC',
  'XLY',
  'XLP',
  'XLE',
  'XLF',
  'XLV',
  'XLI',
  'XLB',
  'XLRE',
  'XLK',
  'XLU',
];
